import { Account, RequireOnly } from '@pelicargo/types';
import { createContext, ReactNode, useCallback, useContext, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { ContactTags } from '../../utils/constants';
import { ContactValue } from '../../utils/email';
export type { ContactValue } from '../../utils/email';

type AccountIdAndName = RequireOnly<Account, 'id' | 'name'>;
export type Association = {
  id: string;
  accounts: AccountIdAndName[];
  providers: string[];
  airports: string[];
  has_accounts: boolean;
  has_airports: boolean;
  has_tags: boolean;
  tags: string[];
};

export type ContactHit = {
  associations: Association[];
  created_at: number;
  email: string;
  full_name: string;
  id: string;
  phone: string;
  preferred_name: string;
  updated_at: number;
};

type SimpleContactSearchContextValue = {
  name: string;

  defaultQuery?: string;
  defaultProviderIds?: string[];
  defaultAirportIds?: string[];
  defaultAccounts?: AccountIdAndName[];
  defaultTags?: string[];

  searchPattern: 'AIRLINE' | 'FF';

  onSelectContact: (contact: ContactHit | ContactValue) => void;
  onRemoveContact: (contact: ContactHit | ContactValue) => void;
  checkIsSelected: (contact: ContactHit | ContactValue) => boolean;
  refreshContactSearch: () => Promise<any>;

  selectedContacts?: ContactValue[];
};

export const SimpleContactSearchContext = createContext({} as SimpleContactSearchContextValue);
export const useSimpleContactSearch = () => useContext(SimpleContactSearchContext);

export type SimpleContactSearchProps = {
  // Name in SimpleForm
  name: string;
  // Default Search State
  defaultQuery?: string;
  defaultProviderIds?: string[];
  defaultAirportIds?: string[];
  defaultAccounts?: AccountIdAndName[];
  defaultTags?: ContactTags[];

  defaultSelectedContacts?: ContactValue[];
  children: ReactNode;

  refreshContactSearch: () => Promise<any>;

  searchPattern: 'AIRLINE' | 'FF';
};

export const formatHitToContactValue = ({
  id,
  created_at,
  updated_at,
  full_name,
  preferred_name,
  email,
  phone,
  associations: [association] = [],
}: ContactHit): ContactValue => ({
  id: Number(id),
  created_at: new Date(created_at),
  updated_at: new Date(updated_at),
  full_name,
  preferred_name,
  email,
  phone,
  tags: association?.tags || [],
  accounts: association?.accounts || [],
  providers: association?.providers || [],
  airports: association?.airports || [],
});

export const SimpleContactSearch = ({
  name,
  defaultAccounts,
  defaultProviderIds,
  defaultAirportIds,
  defaultQuery,
  defaultTags,
  defaultSelectedContacts,
  children,
  searchPattern,
  refreshContactSearch,
}: SimpleContactSearchProps) => {
  const selectedContacts = useWatch({ name });
  const { setValue, getValues } = useFormContext();

  useEffect(() => {
    if (defaultSelectedContacts?.length) {
      setValue(name, defaultSelectedContacts, { shouldValidate: true });
    }
  }, [defaultSelectedContacts?.length]);

  const handleSelectContact = useCallback(
    (hit: ContactHit | ContactValue) => {
      const selected = getValues(name);
      const nextContacts = [...(selected || []), formatHitToContactValue(hit as ContactHit)];
      setValue(name, nextContacts, { shouldValidate: true });
    },
    [getValues, name, setValue],
  );

  const handleRemoveContact = useCallback(
    ({ email }: ContactHit | ContactValue) => {
      const nextContacts = selectedContacts?.filter((c: ContactHit | ContactValue) => c.email !== email);
      setValue(name, nextContacts, { shouldValidate: true });
    },
    [name, selectedContacts, setValue],
  );

  const checkIsSelected = useCallback(
    ({ email }: ContactHit | ContactValue) => {
      return selectedContacts?.some((c: ContactHit | ContactValue) => c.email === email);
    },
    [selectedContacts],
  );

  return (
    <SimpleContactSearchContext.Provider
      value={{
        name,
        searchPattern,

        defaultQuery,
        defaultProviderIds,
        defaultAirportIds,
        defaultAccounts,
        defaultTags,

        onSelectContact: handleSelectContact,
        onRemoveContact: handleRemoveContact,
        refreshContactSearch,

        checkIsSelected,
        selectedContacts,
      }}
    >
      {children}
    </SimpleContactSearchContext.Provider>
  );
};
